import React from "react";
import {
  Box,
  Flex,
  Input,
  Spacer,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Space, Spin, Table, TooltipProps } from "antd";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import {
  ColumnsType,
  FilterValue,
  GetPopupContainer,
  SorterResult,
  TableCurrentDataSource,
  TableLocale,
  TablePaginationConfig,
  TableRowSelection,
} from "antd/lib/table/interface";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { get } from "lodash";
import type { TableProps as RcTableProps } from "rc-table/lib/Table";
import { useEffect, useMemo, useState } from "react";
import { RiDeleteBin6Line, RiEditLine } from "react-icons/ri";
import { useDispatch } from "react-redux";
import { useDiscountFilterMaster } from "../../store/discountfilter/reducer";
import DiscountEditDrawer from "../drawer/DiscountEditDrawer";
import DiscountDeleteConfirmation from "../modals/DiscountDeleteConfirmation";
import { GripVertical, MoreVertical } from "lucide-react";
import axios from "axios";

export interface TableProps<RecordType>
  extends Omit<
    RcTableProps<RecordType>,
    | "transformColumns"
    | "internalHooks"
    | "internalRefs"
    | "data"
    | "columns"
    | "scroll"
    | "emptyText"
  > {
  dropdownPrefixCls?: string;
  dataSource?: RcTableProps<RecordType>["data"];
  columns?: ColumnsType<RecordType>;
  pagination?: false | TablePaginationConfig;
  loading?: boolean;
  size?: SizeType;
  bordered?: boolean;
  locale?: TableLocale;
  onChange?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<RecordType> | SorterResult<RecordType>[],
    extra: TableCurrentDataSource<RecordType>
  ) => void;
  rowSelection?: TableRowSelection<RecordType>;
  getPopupContainer?: GetPopupContainer;
  scroll?: RcTableProps<RecordType>["scroll"] & {
    scrollToFirstRowOnChange?: boolean;
  };
  sortDirections?: string[];
  showSorterTooltip?: boolean | TooltipProps;
}

interface Props {
  query: string;
}

const SortableRow = ({ children, ...props }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === "drag-handle") {
          return React.cloneElement(child as React.ReactElement, {
            children: (
              <div
                {...attributes}
                {...listeners}
                className="cursor-move flex items-center justify-center"
                style={{ width: 24, height: 24 }}
              >
                <GripVertical size={16} className="text-gray-400" />
              </div>
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const DiscountFilterMasterTable: React.FC<Props> = ({ query }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { masterDiscountfilter, busy } = useDiscountFilterMaster();
  const searchColumns = useMemo(() => ["discount_percentage"], []);
  const [searchQuery, setSearchQuery] = useState("");
  console.log("masterDiscountfilter", masterDiscountfilter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const search = (restaurant: Record<string, any>) => {
    return searchColumns.some((column) => {
      return (
        get(restaurant, column, "")
          .toString()
          .toLowerCase()
          .indexOf(searchQuery.toLowerCase()) > -1
      );
    });
  };

  const [targetData, setTargetData] = useState<any>();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const deletes = (record: any) => {
    setTargetData(record);
    onDeleteOpen();
  };

  const update = (record: any) => {
    setTargetData(record);
    onOpen();
  };

  useEffect(() => {
    if (masterDiscountfilter) {
      const transformedData = masterDiscountfilter.map(
        (value: any, index: number) => ({
          _id: value._id,
          key: value._id,
          index: index + 1,
          order: value.sequence,
          category: value.category,
          discount_percentage: value.discount_percentage,
          status: value.status,
          hidden: value.hidden,
          value: value,
        })
      );
      const filtered = searchQuery
        ? transformedData.filter(search)
        : transformedData.sort((a: any, b: any) => a.order - b.order);
      setFilteredData(filtered);
    }
    setLoading(false);
  }, [masterDiscountfilter, searchQuery]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const sendUpdatedOrder = async (reorderedArray: any[]) => {
    const payload = {
      sortingOrder: reorderedArray.map((item) => ({
        key: item.key,
        sequence: item.order,
      })),
    };

    console.log("Sending payload:", payload);

    try {
      const response = await axios({
        method: "patch",
        url: `${process.env.REACT_APP_BASE_URL}/admin/discount/update-order`,
        data: payload,
        withCredentials: true,
      });
      console.log("Update order response:", response);
      toast({
        title: "Success",
        description: "Order updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const handleDragEnd = ({ active, over }: any) => {
    if (active.id !== over?.id) {
      setFilteredData((previous) => {
        const activeIndex = previous.findIndex((i) => i.key === active.id);
        const overIndex = previous.findIndex((i) => i.key === over.id);

        if (activeIndex === -1 || overIndex === -1) {
          console.error("Could not find items to reorder", { active, over });
          return previous;
        }

        const newArray = arrayMove(previous, activeIndex, overIndex);
        const reorderedArray = newArray.map((item, index) => ({
          ...item,
          order: index + 1,
        }));
        console.log("Updated table data after drag:", reorderedArray);
        sendUpdatedOrder(reorderedArray);
        return reorderedArray;
      });
    }
  };

  const columns = [
    {
      width: 50,
      key: "drag-handle",
      render: () => null,
      className: "drag-visible",
    },
    {
      title: "Nos.",
      dataIndex: "index",
      width: 60,
      render: (_: any, __: any, index: number) => `#${index + 1}`,
    },
    {
      title: "Discounts",
      dataIndex: "discount_percentage",
      width: 240,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 150,
      render: (status: string) => (
        <span
          style={{
            width: "113px",
            height: "30px",
            padding: "6px 16px",
            borderRadius: "6px",
            backgroundColor:
              status === "publish"
                ? "rgba(2, 164, 50, 0.10)"
                : "rgba(212, 47, 47, 0.10)",
            color: status === "publish" ? "#02A432" : "#D42F2F",
            fontWeight: "400",
            fontSize: "14px",
          }}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_: any, record: any) => (
        <Space size="middle">
          <RiEditLine
            style={{
              color: "#212121",
              cursor: "pointer",
              height: "20px",
              width: "20px",
            }}
            onClick={() => update(record.value)}
          />
          <RiDeleteBin6Line
            style={{
              color: "#212121",
              cursor: "pointer",
              height: "20px",
              width: "20px",
            }}
            onClick={() => deletes(record)}
          />
        </Space>
      ),
    },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <>
      <div
        style={{
          width: "96vw",
          maxWidth: "96vw",
          maxHeight: "86vh",
          height: "86vh",
          top: "8vh",
          left: "4vw",
          overflowY: "auto",
          position: "relative",
          backgroundColor: "#EEEEEE",
          padding: "30px",
        }}
      >
        <style>
          {`
          .drag-visible {
            width: 48px !important;
            min-width: 48px !important;
            padding: 16px 8px !important;
          }
          .ant-table-cell {
            vertical-align: middle !important;
          }
        `}
        </style>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Flex w="full" alignItems="center" justifyContent="space-between">
            <Box
              display="flex"
              alignItems="center"
              bg="#FFF"
              border="1px solid #b2b2b233"
              borderRadius="12px"
              w="498px"
              padding="8px"
            >
              <Box
                pl={2}
                display="flex"
                alignItems="center"
                color="#9E9E9E"
                fontSize="2xl"
              >
                <img
                  src="/icons/searchlogo.svg"
                  alt="searchlogo"
                  style={{ height: "24px", width: "24px" }}
                />
              </Box>

              <Input
                id="search"
                type="text"
                placeholder="Search or type a command"
                onChange={(e) => handleSearch(e.target.value)}
                fontSize="sm"
                border="none"
                outline="none"
                color="#757575"
                _focus={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                }}
                bg={"transparent"}
                style={{
                  outline: "none",
                  boxShadow: "none",
                }}
              />
            </Box>

            <Spacer />

            <Box
              background="#E24441"
              fontSize={"sm"}
              color="#fff"
              w="fit-content"
              textAlign="center"
              p={3}
              cursor="pointer"
              borderRadius="8px"
              _hover={{ background: "#E24441" }}
              onClick={() => {
                onOpen();
                setTargetData(null);
              }}
            >
              + Add discount filter
            </Box>
          </Flex>

          <Spacer></Spacer>
          {loading ? (
            <Spin size="large" />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredData.map((item) => item.key)}
                strategy={verticalListSortingStrategy}
              >
                <Table
                  components={{
                    body: {
                      row: SortableRow,
                    },
                  }}
                  rowKey="key"
                  columns={columns}
                  dataSource={filteredData}
                  scroll={{ x: "max-content" }}
                  size="middle"
                  pagination={{
                    position: ["bottomRight"],
                    pageSize: 9,
                  }}
                  style={{
                    backgroundColor: "white",
                    borderRadius: 6,
                  }}
                  loading={busy && filteredData.length === 0}
                />
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {isOpen && (
        <DiscountEditDrawer
          isOpen={isOpen}
          onClose={onClose}
          discountData={targetData}
        />
      )}

      {isDeleteOpen && targetData && (
        <DiscountDeleteConfirmation
          isOpen
          onClose={onDeleteClose}
          discountData={targetData}
        />
      )}
    </>
  );
};

export default DiscountFilterMasterTable;
